import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StorageExplorer = () => {
  const [examData, setExamData] = useState([]);
  const [subjectData, setSubjectData] = useState([]);
  const [selectedExamName, setSelectedExamName] = useState('');
  const [showSubjects, setShowSubjects] = useState(false);
  const [unit, setUnit] = useState('MB');

  const chartRef = useRef(null);
  const token = `${localStorage.getItem('token')}`;
  const apiUrl = process.env.REACT_APP_API_URL;

  const toggleUnit = () => {
    setUnit((prev) => (prev === 'MB' ? 'GB' : 'MB'));
  };

  const convertSize = (sizeMB) => {
    return unit === 'MB' ? sizeMB : parseFloat((sizeMB / 1024).toFixed(2));
  };

  useEffect(() => {
    axios
      .get(`${apiUrl}/api/storage/`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const exams = res.data.storageOf.Exams;
        setExamData(exams);
      })
      .catch((err) => console.error(err));
  }, []);

  const fetchSubjects = (examId, examName) => {
    axios
      .get(`${apiUrl}/api/storage/${examId}`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        const subjects = res.data.storageOf.Subjects;
        setSubjectData(subjects);
        setSelectedExamName(examName);
        setShowSubjects(true);
      })
      .catch((err) => console.error(err));
  };

  const handleExamClick = (event) => {
    const index = event.chart.getElementsAtEventForMode(
      event.native,
      'nearest',
      { intersect: true },
      true
    )[0]?.index;

    if (index !== undefined) {
      const selectedExam = examData[index];
      fetchSubjects(selectedExam.id, selectedExam.name);
    }
  };

  const getSuggestedMax = (dataArray) => {
    if (!dataArray.length) return 0;
    const values = dataArray.map((item) =>
      unit === 'GB' ? parseFloat((item.sizeMB / 1024).toFixed(2)) : item.sizeMB
    );
    return unit === 'GB' ? Math.max(10, ...values) : Math.max(1000, ...values);
  };

  const examChart = {
    labels: examData.map((e) => e.name),
    datasets: [
      {
        label: `Exam Storage (${unit})`,
        data: examData.map((e) => convertSize(e.sizeMB)),
        backgroundColor: '#36A2EB',
        barThickness: 30,
      },
    ],
  };

  const subjectChart = {
    labels: subjectData.map((s) => s.name),
    datasets: [
      {
        label: `Subject Storage (${unit})`,
        data: subjectData.map((s) => convertSize(s.sizeMB)),
        backgroundColor: '#FF6384',
        barThickness: 30,
      },
    ],
  };

  const examOptions = {
    onClick: handleExamClick,
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Click on an exam to view its subjects',
      },
      tooltip: {
        callbacks: {
          label: (context) => `Storage: ${context.parsed.y} ${unit}`,
          afterLabel: (context) => {
            const exam = examData[context.dataIndex];
            return `Files: ${exam.filecount}`;
          },
        },
      },
    },
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement.length
        ? 'pointer'
        : 'default';
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: getSuggestedMax(examData),
      },
    },
  };

  const subjectOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: `Subjects under "${selectedExamName}"`,
      },
      tooltip: {
        callbacks: {
          label: (context) => `Storage: ${context.parsed.y} ${unit}`,
          afterLabel: (context) => {
            const subject = subjectData[context.dataIndex];
            return `Files: ${subject.filecount}`;
          },
        },
      },
    },
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement.length
        ? 'pointer'
        : 'default';
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: getSuggestedMax(subjectData),
      },
    },
  };

  return (
    <section className="section dashboard mt-1">
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">
                Reports{' '}
                <span>
                  {' '}
                  /{' '}
                  <button onClick={toggleUnit} className="btn btn-link p-0">
                    {unit === 'MB' ? 'GB' : 'MB'}
                  </button>
                </span>
              </h5>

              {!showSubjects ? (
                <Bar
                  key={`${unit}-exam`}
                  ref={chartRef}
                  data={examChart}
                  options={examOptions}
                />
              ) : (
                <>
                  <Bar
                    key={`${unit}-subject`}
                    ref={chartRef}
                    data={subjectChart}
                    options={subjectOptions}
                  />
                  <button
                    onClick={() => setShowSubjects(false)}
                    className="btn btn-primary mt-3 btn-sm"
                  >
                    <i className="bi bi-box-arrow-in-left"></i>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StorageExplorer;
